import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createUserDialogueWithUserNickname, getUserDialoguesByPage, getUserDialoguesMessagesByPage, patchDialogueMessagesById } from "../../api/sessionAPI";
import { AuthHeaders, Dialogue, DialogueMember, DialogueMessage } from "../../#interfaces/interfaces";

export interface GetUserDialogueMessagesRequestParams {
  authHeaders: AuthHeaders;
  urlParams: GetUserDialoguesMessagesUrlParams;
}

export interface GetUserDialoguesMessagesUrlParams {
  dialogueId: number;
  page: number;
}

export interface GetUserDialoguesRequestParams{
  authHeaders: AuthHeaders;
  urlParams: GetUserDialoguesUrlParams;
}

export interface GetUserDialoguesUrlParams{
  page: number;
}

export interface PostUserDialogueRequestParams{
  authHeaders: AuthHeaders;
  urlParams: PostUserDialogueUrlParams;
  body: PostUserDialogueBody;
}

export interface PostUserDialogueUrlParams{
  userNickname: string;
  currentUserNickname: string;
}

export interface PostUserDialogueBody{
  message: string;
}

export interface SelectDeselectDialogue{
  nickname: string;
}

export interface CreateDialogue{
  nickname: string;
  message?: string;
}

export interface OpenCloseDialogue{
  nickname: string;
}

export interface GetUserNotificationsUrlParams{
    currentUserId?: number;
}

export interface PatchDialogueMessageRequestParams{
  authHeaders: AuthHeaders;
  urlParams: PatchDialogueMessageUrlParams;
  requestBody: PatchDialogueMessageBody;
}

export interface PatchDialogueMessageUrlParams{
  userNickname: string;
  dialogueMessageId: number;
}

export interface PatchDialogueMessageBody{
  visualized: boolean
}

interface UserDialogues {
    selectedDialogues: string[];
    userDialogues: Dialogue[];
    openedDialogueBox: Dialogue[];
    loadingNewSentMessage: boolean;
    loadingMoreDialogueMessages: boolean;
    lastMessagesPage: boolean;
    loadingMoreDialogues: boolean;
}

const initialState: UserDialogues = {
    selectedDialogues: JSON.parse(localStorage.getItem('selectedDialogues') || '[]'),
    userDialogues: [],
    openedDialogueBox: [],
    loadingNewSentMessage: false,
    loadingMoreDialogueMessages: false,
    lastMessagesPage: false,
    loadingMoreDialogues: false
}

export const getDialogueMessages = createAsyncThunk(
  'sessionDialogues/getDialogueMessages',
  async (payload: GetUserDialogueMessagesRequestParams, {rejectWithValue}) => {
      const response = await getUserDialoguesMessagesByPage(
          payload.authHeaders,
          payload.urlParams
      )
      if (response.status >= 200 && response.status < 300) {
          return response.data 
      } else {
        return rejectWithValue(response.data)
      }
  }
)

export const getUserDialogues = createAsyncThunk(
    'sessionDialogues/getUserDialogues',
    async (payload: GetUserDialoguesRequestParams, {rejectWithValue}) => {
        const response = await getUserDialoguesByPage(
            payload.authHeaders,
            payload.urlParams
        )
        if (response.status >= 200 && response.status < 300) {
            return response.data 
        } else {
          return rejectWithValue(response.data)
        }
    }
)

export const updateDialogueMessage = createAsyncThunk(
  'sessionDialogues/updateDialogueMessage',
  async (payload: PatchDialogueMessageRequestParams, {rejectWithValue}) => {
      const response = await patchDialogueMessagesById(
          payload.authHeaders,
          payload.urlParams,
          payload.requestBody
      )
      if (response.status >= 200 && response.status < 300) {
          return response.data 
      } else {
        return rejectWithValue(response.data)
      }
  }
)

export const createUserDialogue = createAsyncThunk(
  'sessionDialogues/createUserDialogue',
  async (payload: PostUserDialogueRequestParams, {rejectWithValue}) => {
      const response = await createUserDialogueWithUserNickname(
          payload.authHeaders,
          payload.urlParams,
          payload.body
      )
      if (response.status >= 200 && response.status < 300) {
          return response.data 
      } else {
        return rejectWithValue(response.data)
      }
  }
)

const dialoguesSlice = createSlice({
    name: 'sessionDialogues',
    initialState,
    reducers: {
      selectDialogue: (state, action: PayloadAction<SelectDeselectDialogue>) => {
        state.selectedDialogues = [...state.selectedDialogues, action.payload.nickname];
        localStorage.setItem('selectedDialogues', JSON.stringify(state.selectedDialogues));
      },
      deselectDialogue: (state, action: PayloadAction<SelectDeselectDialogue>) => {
        state.selectedDialogues = state.selectedDialogues.filter(
          nickname => nickname !== action.payload.nickname
        );
        localStorage.setItem('selectedDialogues', JSON.stringify(state.selectedDialogues));
      },
      openCloseDialogueBox: (state, action: PayloadAction<OpenCloseDialogue>) => {
      
        const matchingDialogue = state.userDialogues.find(userDialogue => 
          userDialogue.dialogueMembers?.some(member => member.user?.nickname === action.payload.nickname)
        );

        const matchingOpenedDialogue = state.openedDialogueBox.find(userDialogue => 
          userDialogue.dialogueMembers?.some(member => member.user?.nickname === action.payload.nickname)
        );
      
        if (matchingDialogue && !state.openedDialogueBox.includes(matchingDialogue) && !matchingOpenedDialogue) {
          state.openedDialogueBox = [...state.openedDialogueBox, matchingDialogue];
        } else if (matchingOpenedDialogue) {

          state.openedDialogueBox = state.openedDialogueBox.filter(openedDialogue => 
            !openedDialogue.dialogueMembers?.some(dialogueMember => dialogueMember.user?.nickname === action.payload.nickname)
          );
      
        } else {
          state.openedDialogueBox = [...state.openedDialogueBox, {
            dialogueMessages: [],
            dialogueMembers: [
                {
                    user: {
                        nickname: action.payload.nickname
                    }
                }
            ]
        }];
        }
      },
      closeAllDialogueBoxes: (state) => {
        state.selectedDialogues = [];
        localStorage.removeItem('selectedDialogues');
      },
      incrementListenedDialogue: (state, action: any) => {

        const convertedDialogue = convertKeysToCamelCaseAndSortByUpdatedAt(action.payload);

        const userSenderNickname = convertedDialogue.dialogueMessages[0].sender.nickname 
        const userRecipientNickname = convertedDialogue.dialogueMessages[0].recipient.nickname


        const index = state.userDialogues.findIndex(dialogue => 
            dialogue.dialogueMembers?.every(member => 
              member.user?.nickname === userSenderNickname || member.user?.nickname === userRecipientNickname
            )
          );

        if (index !== -1) {

            state.userDialogues[index].dialogueMessages?.push(convertedDialogue.dialogueMessages[0]);
            state.loadingNewSentMessage=false;
          } else {
  
            state.userDialogues.push({
              dialogueMembers: [
                  { user: { nickname: userSenderNickname } },
                  { user: { nickname: userRecipientNickname } }
              ],
              dialogueMessages: [convertedDialogue.dialogueMessages[0]]
            });
          }
      }
    },
    extraReducers: (builder) => {
      builder
      .addCase(getUserDialogues.pending, (state, action: any) => {
        if (action.meta.arg.urlParams.page <=1){
          state.userDialogues = [];  
        }
        state.loadingMoreDialogues = true;
      })
      .addCase(getUserDialogues.fulfilled, (state, action: any) => {
        const actionCOnverted = convertKeysToCamelCase(action)

        if (actionCOnverted.meta.arg.urlParams.page <= 1){
          state.userDialogues = convertKeysToCamelCaseAndSortByUpdatedAt(action.payload);
        } else {
          state.userDialogues = [...state.userDialogues, ...convertKeysToCamelCaseAndSortByUpdatedAt(action.payload)];
        }
        state.loadingMoreDialogues = false;
      })
      .addCase(getUserDialogues.rejected, (state) => {
        state.loadingNewSentMessage=true;
        state.loadingMoreDialogues = false;
      })
      .addCase(getDialogueMessages.pending, (state, action: any) => {
        state.loadingMoreDialogues = true;
      })
      .addCase(getDialogueMessages.fulfilled, (state, action: any) => {
        
        if (action.meta.arg.urlParams.page > 1){
          const convertedPayload = convertKeysToCamelCaseAndSortByUpdatedAt(action.payload);
          const dialogueIndex = state.userDialogues.findIndex((userDialogue: Dialogue) => userDialogue.id === convertedPayload.id);
          
          if (dialogueIndex !== -1) {
            const existingMessages = state.userDialogues[dialogueIndex].dialogueMessages ?? [];
            state.userDialogues[dialogueIndex].dialogueMessages = [
              ...convertedPayload.dialogueMessages,
              ...existingMessages,
            ];
          }
        }

        state.loadingMoreDialogues = false;
      })
      .addCase(getDialogueMessages.rejected, (state) => {
        state.loadingNewSentMessage=true;
        state.loadingMoreDialogues = false;
      })

      .addCase(createUserDialogue.pending, (state, action: any) => {
        const convertedDialogue = convertKeysToCamelCaseAndSortByUpdatedAt(action);

        const { userNickname, currentUserNickname } = action.meta.arg.urlParams;

        const index = state.userDialogues.findIndex(dialogue => 
          dialogue.dialogueMembers?.every(member => 
            member.user?.nickname === userNickname || member.user?.nickname === currentUserNickname
          )
        );
        const newMessage = {
          sender: {nickname: convertedDialogue.meta.arg.urlParams.currentUserNickname},
          recipient: {nickname: convertedDialogue.meta.arg.urlParams.userNickname},
          message: convertedDialogue.meta.arg.body.message,
          sent: false
        };

        if (index !== -1) {

          state.userDialogues[index].dialogueMessages?.push(newMessage);
          state.loadingNewSentMessage=false;
        } else {

          state.userDialogues.push({
            dialogueMembers: [
                { user: { nickname: currentUserNickname } },
                { user: { nickname: userNickname } }
            ],
            dialogueMessages: [newMessage]
        });
          state.loadingNewSentMessage=false;
        }

      })
      .addCase(createUserDialogue.fulfilled, (state, action: any) => {
        const convertedDialogue = convertKeysToCamelCaseAndSortByUpdatedAt(action.payload);
      
        const dialogueIndex = state.userDialogues.findIndex(dialogue =>
          dialogue.dialogueMembers?.length === convertedDialogue.dialogueMembers.length &&
          dialogue.dialogueMembers?.every(dialogueMember =>
            convertedDialogue.dialogueMembers.some(
              (newDialogueMember: DialogueMember) => newDialogueMember.user?.nickname === dialogueMember.user?.nickname
            )
          )
        );
      
        if (dialogueIndex !== -1) {
          state.userDialogues[dialogueIndex].dialogueMessages = state.userDialogues[dialogueIndex].dialogueMessages?.filter(
            (dialogueMessage: any) => dialogueMessage.createdAt
          );
          
          state.userDialogues[dialogueIndex].dialogueMessages?.push(convertedDialogue.dialogueMessages[0]);
        } else {
          console.warn(`Dialogue with matching dialogueMembers not found`);
        }
      })      
      .addCase(createUserDialogue.rejected, (state) => {
        console.log("getUserDialogues.rejected")
      })
      .addCase(updateDialogueMessage.fulfilled, (state, action: any) => {

        const payloadCamelCase = convertKeysToCamelCase(action.payload);
        const dialogue = state.userDialogues.find(
          (userDialogue: Dialogue) => userDialogue.id === payloadCamelCase[0].dialogueId
        );
        
        if (dialogue && dialogue.dialogueMessages) {
          dialogue.dialogueMessages = dialogue.dialogueMessages.map((message) => ({
            ...message,
            lastAlienMessageVisualized: true,
            visualized: true,
          }));
        }
      })
      .addCase(updateDialogueMessage.rejected, (state) => {
        console.warn("updateDialogueMessage rejected")
      })
      }
    })

export const { selectDialogue, deselectDialogue, 
               openCloseDialogueBox, closeAllDialogueBoxes } = dialoguesSlice.actions;

export const dialoguesSliceReducers = dialoguesSlice.reducer;
export const dialoguesSliceActions = dialoguesSlice.actions;

export function convertKeysToCamelCaseAndSortByUpdatedAt(obj: any): any {
  function convertKeysToCamelCase(inputObj: any): any {
    if (typeof inputObj === 'object' && inputObj !== null) {
      if (Array.isArray(inputObj)) {
        return inputObj.map(item => convertKeysToCamelCase(item));
      } else {
        if (inputObj.constructor === Object) {
          const newObj: { [key: string]: any } = {};
          for (const key in inputObj) {
            if (Object.prototype.hasOwnProperty.call(inputObj, key)) {
              const camelCaseKey = key.replace(/_([a-z])/g, (match, letter) =>
                letter.toUpperCase()
              );
              newObj[camelCaseKey] = convertKeysToCamelCase(inputObj[key]);
            }
          }
          return newObj;
        }
      }
    }
    return inputObj;
  }

  const camelCasedObj = convertKeysToCamelCase(obj);

  if (Array.isArray(camelCasedObj)) {
    return camelCasedObj.sort((a, b) => {
      const dateA = new Date(a.updatedAt);
      const dateB = new Date(b.updatedAt);
      return dateB.getTime() - dateA.getTime();
    });
  } else {
    return camelCasedObj;
  }
}

export function convertKeysToCamelCase(obj: any): any {
  if (typeof obj === 'object' && obj !== null) {
    if (Array.isArray(obj)) {
      return obj.map(item => convertKeysToCamelCase(item));
    } else if (obj.constructor === Object) {
      const newObj: { [key: string]: any } = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const camelCaseKey = key.replace(/_([a-z])/g, (_, letter) =>
            letter.toUpperCase()
          );
          newObj[camelCaseKey] = convertKeysToCamelCase(obj[key]);
        }
      }
      return newObj;
    }
  }
  return obj;
}